import React from 'react';

import type { TCapacityTone, TCapacityGlyph } from './types';

const HALF_DISC = 'M6 1.4 A4.6 4.6 0 0 0 6 10.6 Z';

const TONE_VAR: Record<TCapacityTone, string> = {
  free: 'var(--capacity-free)',
  busy: 'var(--capacity-busy)',
  idle: 'var(--capacity-idle)',
  pending: 'var(--capacity-idle-line)',
};

interface IProps {
  glyph: TCapacityGlyph;
  tone: TCapacityTone;
}

/**
 * 12px status glyph. Four silhouettes — solid disc, half disc, hollow ring,
 * dashed ring — so the state survives greyscale, glare and colour blindness.
 * Colour is reinforcement only, never the sole signal.
 *
 * aria-hidden: the meaning is already in the visually hidden sentence rendered
 * by the parent. Announcing it twice is worse than not at all.
 */
const CapacityGlyph: React.FC<IProps> = ({ glyph, tone }) => {
  const color = TONE_VAR[tone];
  const isSolid = glyph === 'solid';

  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="6"
        cy="6"
        r={isSolid ? 5 : 4.6}
        fill={isSolid ? color : 'none'}
        stroke={isSolid ? 'none' : color}
        strokeWidth="1.8"
        strokeDasharray={glyph === 'dashed' ? '2.5 2.2' : undefined}
      />
      {glyph === 'half' && <path d={HALF_DISC} fill={color} />}
    </svg>
  );
};

export default CapacityGlyph;
